import type { FeedbackPosition, SurveyResponse } from '@lms/api-client/backend';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { useBrand } from '@/features/branding/brand-provider';
import { answerSurvey } from '@/features/scormplayer/survey-api';

export default function TakeSurveyForm({
  survey,
  sessionId,
  position,
}: {
  survey: SurveyResponse;
  sessionId?: string;
  position?: FeedbackPosition;
}) {
  const { slug } = useBrand();
  const navigate = useNavigate();
  const locale = useLocale();
  const t = useTranslations('survey');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const questionKey =
    position === 'before_course' ? 'beforeQuestion' : 'afterQuestion';

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!sessionId || !position) {
      return;
    }

    const formData = new FormData(event.currentTarget);
    const answers = Object.entries(Object.fromEntries(formData))
      .filter(([k]) => k.startsWith('q-'))
      .map(([k, v]) => ({
        questionId: Number.parseInt(k.replace('q-', ''), 10),
        answer: Number.parseInt(v.toString(), 10),
      }));

    setPending(true);
    try {
      const result = await answerSurvey({
        slug,
        session: sessionId,
        survey: survey.id,
        position,
        answers,
      });
      if (result.kind === 'redirect') {
        navigate(result.to);
      } else {
        setError(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex max-w-screen-md flex-col gap-8">
      <ol className="list-outside list-decimal pl-8">
        {survey.questions
          .filter((n) => n.beforeQuestion !== null)
          .sort((a, b) => a.order - b.order)
          .map((n) => ({
            id: n.id,
            question: n[questionKey][locale] ?? n[questionKey].en,
          }))
          .map(({ id, question }) => (
            <li key={id} className="mb-8 list-item">
              <div>{question}</div>
              <div className="mt-4 grid border-collapse grid-cols-10 gap-0 text-center">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <label
                    key={`${id}-${rating}`}
                    className="has-[:checked]:bg-accent border px-3 py-2"
                  >
                    <input
                      type="radio"
                      name={`q-${id}`}
                      value={`${rating}`}
                      className="appearance-none"
                    />
                    {rating}
                  </label>
                ))}
              </div>
            </li>
          ))}
      </ol>
      {error && <div className="text-red-500">{error}</div>}
      <SubmitButton label={t('submit_answers')} pending={pending} />
    </form>
  );
}
